
  import { Component, Vue, Prop } from 'vue-property-decorator';
  import { mapGetters } from 'vuex';

  import { IErrorModel } from '@/models/errors.model';
  import { IGetLoginPartnerModel } from '@/models/login.model';
  import { IPartnerLoginResultModel, IPartnerRegisterModel, IPartnerRegisterResultModel } from '@/models/register.model';

  import Input from '@/components/Core/Input.vue';
  import Checkbox from '@/components/Core/Checkbox.vue';

  import accountService from '@/services/account.service';
  import cookieService from '../../services/cookie.service';
  import errorsService from '@/services/errors.service';
  import recaptchaService from '@/services/recaptcha.service';
  import tokenService from '@/services/token.service';

  @Component({
    components: {
      Input,
      Checkbox
    },
    computed: {
      ...mapGetters([
        'isLoading',
        'clientId',
        'SSOId',
        'SSOEmail',
        'SSORegisterCode',
        'username',
        'cookies',
        'privacyLinks',
      ])
    }
  })
  export default class PartnerRegister extends Vue {
    public data() {
      return {
        studio: '',
        environment: '',
        isRegistering: false,
        isFast: false,
        isUsernameReadOnly: false,
        showEmailInput: true,
        timeOnPage: new Date().getTime(),
        errors: new Array<IErrorModel>(),
        form: { Partner: "" } as IPartnerRegisterModel,
        retryLink: "",
        EULALink: "",
        privacyPolicyLink: "",
        privacyPolicyContent: "",
        EULAContent: "",
        codeOfConductContent: "I agree to <a href=\"https://community.companyofheroes.com/coh-franchise-home/company-of-heroes-3/forums/2-welcome-info/threads/1-code-of-conduct-code-of-heroes?page=1#post-1\" target=\"_blank\" title=\"Code of Conduct\">the code of conduct."
      }
    };

    public created(): void {
      this.$data.studio = this.$data.RCEnvironment.VUE_APP_PARTNER_NAME;
      this.$data.form.Email = this.$store.getters.SSOEmail;
      this.$data.form.Username = this.$store.getters.username;
      this.$data.form.Id_SSO = this.$store.getters.SSOId;
      this.$data.form.SSORegisterCode = this.$store.getters.SSORegisterCode;
      this.$data.form.Partner = this.$data.RCEnvironment.VUE_APP_PARTNER_NAME;
      this.$data.environment = this.$data.RCEnvironment.VUE_APP_ENVIRONMENT;

      this.initPrivacyLinks();

      if (this.$data.environment == "CREATIVE-ASSEMBLY") {
        this.$data.isUsernameReadOnly = true;
      } else if (this.$data.environment == "FOCUS") {
        this.$data.isUsernameReadOnly = true;
      } else if (this.$data.environment == "STILLFRONT") {
        this.$data.isUsernameReadOnly = true;
      } else if (this.$data.environment == "UBISOFT-PLAYER-COUNCIL") {
        this.$data.isUsernameReadOnly = true;
        this.$data.showEmailInput = false;
      }
    }

    public mounted(): void {
      this.$watch(() => this.$store.getters.privacyLinks, (params) => {
        this.initPrivacyLinks();
      });
    }

    public getFilteredErrors(property: string = '') {
      return (this.$data.errors as Array<IErrorModel>).filter(error => error.Property == property);
    }

    public register() {
      this.$data.errors = [];
      let timer: number = 15000;
      this.$data.isFast = false;

      if (this.$data.isRegistering || this.$data.form.Additional) {
        return;
      }

      if (this.$data.environment != "CREATIVE-ASSEMBLY" && this.$data.environment != "FOCUS" && this.$data.environment != "STILLFRONT" && this.$data.environment != "UBISOFT-PLAYER-COUNCIL" && (new Date().getTime() - this.$data.timeOnPage < timer)) {
        this.$data.isFast = true;
        this.$data.timeOnPage = new Date().getTime();
        return;
      }

      this.$data.isRegistering = true;
      this.$store.dispatch("updateIsLoading", true);

      this.$data.errors = new Array<IErrorModel>();

      if (this.$data.RCEnvironment.VUE_APP_ENABLE_RECAPTCHA == true) {
        recaptchaService.getToken(this, "register").then(token => {
          this.$data.form.Token = token;

          setTimeout(() => {
            recaptchaService.verify(this, this.$data.form.Token)
              .then(response => {
                if (!response.Success) {
                  this.$data.isRegistering = false;
                  this.$data.errors = [{ Property: "", ErrorMessage: "An error occured. Please try again later." } as IErrorModel];
                  this.$store.dispatch("updateIsLoading", false);
                  return;
                }

                if (!response.IsValid) {
                  this.$data.isRegistering = false;
                  this.$data.errors = [{ Property: "", ErrorMessage: "You're not allowed to create an account." } as IErrorModel];
                  this.$store.dispatch("updateIsLoading", false);
                  return;
                }

                this.registerRequest();
              });
          }, 300);
        }).catch(errors => {
          this.$data.isRegistering = false;
        });
      } else {
        this.registerRequest();
      }
    };

    public updateStringValue(parameterName: string, value: string) {
      this.$data.form[parameterName] = value;
    }

    public updateBooleanValue(parameterName: string, value: boolean) {
      this.$data.form[parameterName] = value;
    }

    private registerRequest() {
      const url: string = this.$data.RCEnvironment.VUE_APP_ACCOUNT_API_URL + "partner/user/register";
      this.$data.form.ClientId = this.$store.getters.clientId;
      this.$data.form.Cookies = this.$store.getters.cookies;

      accountService.post<IPartnerRegisterModel, IPartnerRegisterResultModel>(this, url, this.$data.form)
        .then(register => {
          this.$data.isRegistering = false;

          this.triggerEvent(true);

          const now = new Date();
          this.$cookies.set("_g2g_identity_", register.Created, new Date(now.setDate(now.getDate() + 30)), undefined, undefined, undefined, "Strict");

          this.triggerEvent();

          this.$data.isLoginLoading = false;
          tokenService.setToken(this, true);
        })
        .catch(errors => {
          this.setRetryLink();
          this.$data.errors = errors.data.ErrorMessages;
          this.$data.isRegistering = false;
          this.$store.dispatch("updateIsLoading", false);
        });
    }

    private setRetryLink(): void {
      const url = this.$data.RCEnvironment.VUE_APP_ACCOUNT_API_URL + "partner/" + this.$data.RCEnvironment.VUE_APP_PARTNER_NAME + "/register-url";

      accountService.get<string>(this, url)
        .then(response => {
          this.$data.retryLink = response;
        });
    }

    private triggerEvent(isRegisterConfirm: boolean = false) {
      const consentAnalytics = this.$cookies.get("consent_analytics");
      const isConsentAnalyticsAccepted = consentAnalytics != null && consentAnalytics == "1";

      if (!isConsentAnalyticsAccepted) {
        return;
      }

      isRegisterConfirm ? cookieService.triggerPartnerRegisterConfirmEvent(this) : cookieService.triggerFirstSignUpEvent(this);
    }

    private initPrivacyLinks(): void {
      this.$data.privacyPolicyLink = this.$store.getters.privacyLinks.PrivacyPolicy;
      this.$data.EULALink = this.$store.getters.privacyLinks.TermsOfService;

      this.$data.privacyPolicyContent = "I acknowledge that I have read and understood the practices set out in the <a href=\"" + this.$data.privacyPolicyLink + "\" target=\"_blank\" title=\"Privacy Policy\">Privacy Policy</a> (please tick).";
      this.$data.EULAContent = "I accept the <a href=\"" + this.$data.EULALink + "\" target=\"_blank\" title=\"Terms of Service\">Terms of Service</a>.";
    }
  }
